import React, { useState, useEffect } from 'react';
import { PassengerVehicle } from '../../types';
import {
  createPassengerVehicle,
  updatePassengerVehicle,
  PassengerVehicleCreate,
  PassengerVehicleUpdate,
} from '../../services/memberService';
import Modal from '../shared/Modal';

interface PassengerVehicleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  vehicle?: PassengerVehicle | null;
  memberId: number;
}

const PassengerVehicleFormModal: React.FC<PassengerVehicleFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  vehicle,
  memberId,
}) => {
  const isEdit = !!vehicle;

  const [licenseNo, setLicenseNo] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [color, setColor] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (vehicle) {
        setLicenseNo(vehicle.licenseNo || '');
        setMake(vehicle.make || '');
        setModel(vehicle.model || '');
        setColor(vehicle.color || '');
        setActive(vehicle.active);
      } else {
        setLicenseNo('');
        setMake('');
        setModel('');
        setColor('');
        setActive(true);
      }
      setError(null);
    }
  }, [isOpen, vehicle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!licenseNo.trim()) {
      setError('License number is required.');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && vehicle) {
        const data: PassengerVehicleUpdate = {
          license_no: licenseNo.trim(),
          make: make.trim() || undefined,
          model: model.trim() || undefined,
          color: color.trim() || undefined,
          active,
        };
        await updatePassengerVehicle(vehicle.id, data);
      } else {
        const data: PassengerVehicleCreate = {
          license_no: licenseNo.trim(),
          make: make.trim() || undefined,
          model: model.trim() || undefined,
          color: color.trim() || undefined,
        };
        await createPassengerVehicle(memberId, data);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error('Error saving passenger vehicle:', err);
      setError(err.message || 'Failed to save passenger vehicle');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
        {saving ? 'Saving...' : isEdit ? 'Update Vehicle' : 'Add Vehicle'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Passenger Vehicle' : 'Add Passenger Vehicle'}
      size="small"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="form-modal-body">
        {error && <div className="form-error-banner">{error}</div>}

        <div className="form-group">
          <label>License No *</label>
          <input type="text" value={licenseNo} onChange={e => setLicenseNo(e.target.value)} required />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Make</label>
            <input type="text" value={make} onChange={e => setMake(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Model</label>
            <input type="text" value={model} onChange={e => setModel(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Color</label>
          <input type="text" value={color} onChange={e => setColor(e.target.value)} />
        </div>

        {isEdit && (
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={active}
                onChange={e => setActive(e.target.checked)}
                style={{ width: 'auto' }}
              />
              Active
            </label>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default PassengerVehicleFormModal;
